import { nextTick, createApp, h } from 'vue'
import { snapdom } from '@zumer/snapdom'

import { SURFACE_EDITOR_IMAGE_EXPORT_API } from '~/constants/surface-editor.js'

/**
 * The DOM -> image renderer shared by the Surface Editor export (composables/surface-editor/
 * project-export.js) and the framed-print cart thumbnail (composables/framed-prints/thumbnail.js).
 *
 * Mounts a component OFFSCREEN in its own Vue app, waits until everything it paints is really
 * ready - fonts loaded, every <img> and CSS background decoded - then rasterizes it with snapdom.
 * Cross-origin images are rewritten through the same-origin asset proxy first
 * (`SURFACE_EDITOR_IMAGE_EXPORT_API`), because a single tainted image makes the canvas unreadable
 * and the export fails outright rather than degrading.
 *
 * Extracted verbatim from project-export.js so both features render through one pipeline (the
 * Surface Editor's Google Fonts preload is passed in as `prepare`). One behaviour changed for both:
 * same-origin images are no longer routed through the proxy - see `isSameOrigin()`. For the Surface
 * Editor that only affects same-origin images, which the proxy would have refused anyway.
 */

function createOwnedExportMount() {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  el.style.position = 'fixed'
  el.style.left = '-999999px'
  el.style.top = '0'
  el.style.width = '0'
  el.style.height = '0'
  el.style.overflow = 'hidden'
  el.style.pointerEvents = 'none'
  document.body.appendChild(el)
  return el
}

function nextFrame() {
  return new Promise(resolve => requestAnimationFrame(() => resolve()))
}

export function makeLogger(debug) {
  if (!debug) return null
  if (typeof debug === 'function') return debug
  if (debug && typeof debug.log === 'function') return debug.log
  return (...args) => console.debug('[project-export]', ...args)
}

async function decodeOrVerify(img, label, log) {
  if (!img.decode) {
    if (!img.complete || img.naturalWidth <= 0) {
      throw new Error(`Image not usable: ${label}`)
    }
    return
  }

  try {
    await img.decode()
    return
  } catch (e) {
    log?.('decode() rejected; fallback verify:', label, e?.message || e)
    await nextFrame()
    await nextFrame()

    if (!img.complete || img.naturalWidth <= 0) {
      throw new Error(
        `Image decode failed and image is not usable: ${label}. (${e?.message || e})`
      )
    }
  }
}

async function waitForFonts(rootEl, log) {
  if (!document.fonts?.load) return

  const fontSpecs = new Set()

  rootEl.querySelectorAll('*').forEach(el => {
    const cs = getComputedStyle(el)
    if (!cs.fontFamily) return
    fontSpecs.add(`${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`)
  })

  log?.('fonts: specs found', fontSpecs.size)

  await Promise.all(
    Array.from(fontSpecs).map(async (spec) => {
      log?.('fonts: load ->', spec)
      const loaded = await document.fonts.load(spec, 'BESbswy')
      if (!loaded || loaded.length === 0) {
        throw new Error(`Font failed to load (no faces): ${spec}`)
      }
      if (document.fonts.check && !document.fonts.check(spec, 'BESbswy')) {
        throw new Error(`Font check failed: ${spec}`)
      }
      log?.('fonts: ok <-', spec)
    })
  )

  await document.fonts.ready
  log?.('fonts: document.fonts.ready')
}

async function waitForImages(rootEl, log) {
  const promises = []
  const imgs = Array.from(rootEl.querySelectorAll('img'))

  log?.('img: count', imgs.length)

  for (const img of imgs) {
    if (!img.crossOrigin) img.crossOrigin = 'anonymous'

    const src = img.currentSrc || img.src || '(unknown src)'

    if (img.complete) {
      if (img.naturalWidth <= 0) {
        throw new Error(`Image loaded but is broken/empty: ${src}`)
      }
      promises.push(
        (async () => {
          log?.('img: complete ->', src)
          await decodeOrVerify(img, src, log)
          log?.('img: ok <-', src)
        })()
      )
      continue
    }

    promises.push(
      new Promise((resolve, reject) => {
        const onLoad = async () => {
          try {
            if (img.naturalWidth <= 0) {
              reject(new Error(`Image loaded but is broken/empty: ${src}`))
              return
            }
            log?.('img: load ->', src)
            await decodeOrVerify(img, src, log)
            log?.('img: ok <-', src)
            resolve()
          } catch (e) {
            reject(e)
          }
        }

        const onError = () => reject(new Error(`Image failed to load: ${src}`))

        img.addEventListener('load', onLoad, { once: true })
        img.addEventListener('error', onError, { once: true })
      })
    )
  }

  await Promise.all(promises)
  log?.('img: all done')
}

// Same-origin images cannot taint the canvas, so they never need the proxy - and cannot use it:
// the proxy only fetches allow-listed https hosts (server/api/proxy-asset.js), which the app's own
// origin is not (and never is over http://localhost). The framed-print frame/mat art is served
// from the app itself, so without this skip every framed-print render fails on its own assets.
function isSameOrigin(absoluteUrl) {
  return new URL(absoluteUrl).origin === window.location.origin
}

function proxifyUrl(url) {
  const abs = new URL(url, document.baseURI).href
  return `${SURFACE_EDITOR_IMAGE_EXPORT_API}?u=${encodeURIComponent(abs)}`
}

function proxyImgSrcs(rootEl, log) {
  const imgs = Array.from(rootEl.querySelectorAll('img'))

  for (const img of imgs) {
    const raw = img.currentSrc || img.src
    if (!raw) continue
    if (raw.startsWith('data:') || raw.startsWith('blob:')) continue
    if (raw.includes(`${SURFACE_EDITOR_IMAGE_EXPORT_API}?`)) continue

    let abs
    try {
      abs = new URL(raw, document.baseURI).href
    } catch {
      throw new Error(`Malformed img src: ${raw}`)
    }

    if (isSameOrigin(abs)) continue

    const proxied = proxifyUrl(abs)
    img.crossOrigin = 'anonymous'
    img.src = proxied
    log?.('img: rewrite', abs, '->', proxied)
  }
}

function proxyBackgroundImages(rootEl, log) {
  const urlRegex = /url\(["']?([^"')]+)["']?\)/g
  const els = Array.from(rootEl.querySelectorAll('*'))

  for (const el of els) {
    const bg = getComputedStyle(el).backgroundImage
    if (!bg || bg === 'none') continue

    let newBg = bg
    let changed = false

    for (const match of bg.matchAll(urlRegex)) {
      const raw = match[1]
      if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) continue
      if (raw.includes(`${SURFACE_EDITOR_IMAGE_EXPORT_API}?`)) continue

      let abs
      try {
        abs = new URL(raw, document.baseURI).href
      } catch {
        throw new Error(`Malformed background URL: ${raw}`)
      }

      if (isSameOrigin(abs)) continue

      const proxied = proxifyUrl(abs)
      newBg = newBg.replace(raw, proxied)
      changed = true
      log?.('bg: rewrite', abs, '->', proxied)
    }

    if (changed) el.style.backgroundImage = newBg
  }
}

async function waitForBackgroundImages(rootEl, log) {
  const urlRegex = /url\(["']?([^"')]+)["']?\)/g
  const urls = new Set()

  const els = Array.from(rootEl.querySelectorAll('*'))
  for (const el of els) {
    const bg = getComputedStyle(el).backgroundImage
    if (!bg || bg === 'none') continue

    for (const match of bg.matchAll(urlRegex)) {
      const raw = match[1]
      if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) continue

      let abs
      try {
        abs = new URL(raw, document.baseURI).href
      } catch {
        throw new Error(`Malformed background URL: ${raw}`)
      }

      urls.add(abs)
    }
  }

  const list = Array.from(urls)

  const promises = list.map(abs => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = async () => {
        try {
          if (img.naturalWidth <= 0) {
            reject(new Error(`Background image loaded but is broken/empty: ${abs}`))
            return
          }
          log?.('bg: load ->', abs)
          await decodeOrVerify(img, abs, log)
          log?.('bg: ok <-', abs)
          resolve()
        } catch (e) {
          reject(e)
        }
      }

      img.onerror = () => reject(new Error(`Background image failed to load: ${abs}`))
      img.src = abs
    })
  })

  await Promise.all(promises)
  log?.('bg: all done')
}

/**
 * Renders `component` (with `props`) offscreen at exactly `width` x `height` CSS px and returns
 * the snapdom blob.
 *
 * `prepare(container)` runs after the first render and before any waiting - the hook for anything
 * the component needs loaded that the DOM alone cannot reveal (the Surface Editor uses it to
 * request Google Fonts stylesheets).
 *
 * `nuxtApp`, when given, is attached as the export app's `$nuxt` so Nuxt composables inside the
 * component (useRuntimeConfig, useRequestURL, ...) resolve to the host app's instance explicitly,
 * rather than relying on Nuxt's client-side global fallback.
 */
export async function renderComponentToBlob({
  component,
  props = {},
  width,
  height,
  mountEl = null,
  prepare = null,
  nuxtApp = null,
  exportType,
  quality = 0.85,
  snapdomOptions = {},
  debug
} = {}) {
  const log = makeLogger(debug)
  const ownedMount = !mountEl
  const exportMount = mountEl || createOwnedExportMount()

  const exportContainer = document.createElement('div')
  exportContainer.style.width = `${width}px`
  exportContainer.style.height = `${height}px`
  exportContainer.style.position = 'relative'
  exportContainer.style.overflow = 'hidden'
  exportContainer.style.background = 'transparent'
  exportMount.appendChild(exportContainer)

  const app = createApp({
    render() {
      return h(component, props)
    }
  })

  if (nuxtApp) {
    app.$nuxt = nuxtApp
  }

  app.mount(exportContainer)

  try {
    log?.('begin render', { width, height })

    await nextTick()
    await prepare?.(exportContainer, log)
    await waitForFonts(exportContainer, log)

    proxyImgSrcs(exportContainer, log)
    await waitForImages(exportContainer, log)

    proxyBackgroundImages(exportContainer, log)
    await waitForBackgroundImages(exportContainer, log)

    await nextFrame()
    await nextFrame()

    return await snapdom.toBlob(exportContainer, {
      type: exportType || 'jpeg',
      quality,
      embedFonts: true,
      dpr: 1,
      width,
      height,
      ...snapdomOptions
    })
  } finally {
    try {
      app.unmount()
    } catch (err) {
      console.warn('Failed to unmount export app:', err)
    }

    exportContainer.remove()
    if (ownedMount) {
      exportMount.remove()
    }
  }
}
